(ns app.front.view.admin.commands.query-analyzer
  (:require [app.front.ui :as ui]
            [app.front.util :as futil :refer [for-frag for-frag-indexed]]
            [app.front.view.group.admin.common :as acommon]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.config :as config]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

;; -- Events ------------------------------------------------------------------

(rf/reg-event-fx ::execute
  (fn [{:keys [db]} [_ query args]]
    {:db           (-> db
                       (assoc-in [::state :pending?] true)
                       (update ::state dissoc :error :results :result-count
                               :io-stats :query-stats :elapsed-ms))
     :comm/command {:command          :admin/analyze-query
                    :data             (cond-> {:query query}
                                        (seq args) (assoc :args args))
                    :on-success       [::execute.success]
                    :finally-dispatch [::execute.done]}}))

(rf/reg-event-db ::execute.success
  (fn [db [_ _ response]]
    (update db ::state merge (select-keys response [:results :result-count
                                                    :io-stats :query-stats
                                                    :elapsed-ms :error]))))

(rf/reg-event-db ::execute.done
  (fn [db _]
    (assoc-in db [::state :pending?] false)))

(rf/reg-event-db ::clear
  (fn [db _]
    (dissoc db ::state)))

;; -- Subscriptions -----------------------------------------------------------

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::pending?
  :<- [::state]
  (fn [state _]
    (boolean (:pending? state))))

(rf/reg-sub ::results
  :<- [::state]
  :-> :results)

(rf/reg-sub ::result-count
  :<- [::state]
  :-> :result-count)

(rf/reg-sub ::io-stats
  :<- [::state]
  :-> :io-stats)

(rf/reg-sub ::query-stats
  :<- [::state]
  :-> :query-stats)

(rf/reg-sub ::elapsed-ms
  :<- [::state]
  :-> :elapsed-ms)

(rf/reg-sub ::error
  :<- [::state]
  :-> :error)

;; -- View helpers ------------------------------------------------------------

(def ^:private example-query
  "[:find ?e ?type\n :where\n [?e :entity/type ?type]]")

(defn- stat-card [label value]
  [:div.flex.flex-col.px-4.py-2
   [:span.text-xs.text-ui-gray-60 label]
   [:span.font-mono.text-sm value]])

(defn- results-summary []
  (let [elapsed      (<sub [::elapsed-ms])
        result-count (<sub [::result-count])
        results      (<sub [::results])
        showing      (count results)
        truncated?   (< showing result-count)]
    [:div.flex.flex-wrap.gap-2.bg-ui-light-gray.rounded-md.p-2.mb-4
     [stat-card "Elapsed" (str elapsed " ms")]
     [stat-card "Results" (if truncated?
                            (str result-count " (showing first " showing ")")
                            (str result-count))]]))

(defn- io-stats-display []
  (let [io-stats (<sub [::io-stats])]
    (when (seq io-stats)
      [acommon/section {:title "IO Statistics"}
       (let [{:keys [reads]} io-stats
             other-keys      (dissoc io-stats :reads)]
         [:div
          (when (seq reads)
            [:div.overflow-x-auto
             [:table.w-full.text-sm.font-mono
              [:thead
               [:tr.border-b.border-solid
                [:th.text-left.p-2 "Source"]
                [:th.text-right.p-2 "Reads"]]]
              [:tbody
               (for-frag [[k v] (sort-by (comp str first) reads)]
                 [:tr.border-b.border-solid.border-ui-light-gray
                  [:td.p-2.font-semibold (name k)]
                  [:td.text-right.p-2 (str v)]])]]])
          (when (seq other-keys)
            [:div.mt-4
             (for-frag [[k v] (sort-by first other-keys)]
               [:div.flex.gap-2.py-1
                [:span.font-semibold.font-mono (pr-str k)]
                [:span.font-mono (pr-str v)]])])])])))

(defn- query-stats-display []
  (let [query-stats (<sub [::query-stats])]
    (when (seq query-stats)
      [acommon/section {:title "Query Statistics"}
       (let [{:keys [phases]} query-stats]
         [:div
          (for-frag-indexed [[idx phase] phases]
            [:div.mb-6
             [:h4.font-semibold.mb-2 (str "Phase " (inc idx))]
             (when-let [clauses (:clauses phase)]
               [:div.overflow-x-auto
                [:table.w-full.text-sm
                 [:thead
                  [:tr.border-b.border-solid
                   [:th.text-left.p-2 "Clause"]
                   [:th.text-right.p-2 "Rows In"]
                   [:th.text-right.p-2 "Rows Out"]
                   [:th.text-right.p-2 "Binds In"]
                   [:th.text-right.p-2 "Binds Out"]
                   [:th.text-right.p-2 "Preds"]
                   [:th.text-right.p-2 "Expansion"]]]
                 [:tbody
                  (for-frag [clause clauses]
                    [:tr.border-b.border-solid.border-ui-light-gray
                     [:td.p-2.font-mono (pr-str (:clause clause))]
                     [:td.text-right.p-2 (str (:rows-in clause))]
                     [:td.text-right.p-2 (str (:rows-out clause))]
                     [:td.text-right.p-2 (pr-str (:binds-in clause))]
                     [:td.text-right.p-2 (pr-str (:binds-out clause))]
                     [:td.text-right.p-2 (str (:preds clause))]
                     [:td.text-right.p-2 (str (:expansion clause))]])]]])
             (when-let [warnings (:warnings phase)]
               [:div.mt-2.p-2.bg-amber-50.border.border-amber-200.rounded-md.text-sm
                [:span.font-semibold "Warnings: "]
                [:span.font-mono (pr-str warnings)]])])])])))

(defn- render-cell [cell]
  (cond
    (nil? cell) "nil"
    (or (sequential? cell)
        (set? cell)
        (map? cell)
        (keyword? cell)) (pr-str cell)
    :else (str cell)))

(defn- results-table []
  (let [results      (<sub [::results])
        result-count (<sub [::result-count])
        showing      (count results)
        truncated?   (< showing result-count)]
    (when (seq results)
      [acommon/section {:title "Results"}
       [:div
        (when truncated?
          [:p.text-sm.text-ui-gray-60.mb-2
           (str "Showing " showing " of " result-count " results")])
        [:div.overflow-x-auto
         [:table.w-full
          [:tbody
           (for-frag [row results]
             [:tr.border-b.border-solid.border-ui-light-gray
              (for-frag [cell (if (sequential? row) row [row])]
                [:td.p-2.font-mono.text-sm (render-cell cell)])])]]]]])))

;; -- Main components ---------------------------------------------------------

(defn- production-overlay [*acknowledged?]
  [:div.flex.flex-col.items-center.justify-center.py-16.px-8
   [:div.max-w-lg.text-center
    [:div.text-red-600.text-5xl.font-bold.mb-4 "!"]
    [:h2.text-xl.font-bold.text-red-700.mb-4
     "Production Environment"]
    [:p.text-sm.text-gray-700.mb-2
     "You are about to run Datomic queries against the production database."]
    [:p.text-sm.text-gray-700.mb-6
     "Heavy queries can degrade system performance for all users. "
     "Ensure your query is well-scoped with selective :where clauses."]
    (cc/button {:on-click #(reset! *acknowledged? true)
                :kind     :danger
                :size     :field}
               "I understand the risks, show query fields")]])

(defn query-analyzer-panel []
  (r/with-let [*query         (r/atom "")
               *args          (r/atom "")
               *acknowledged? (r/atom false)]
    (let [pending?   (<sub [::pending?])
          error      (<sub [::error])
          results    (<sub [::results])
          needs-ack? (and (= (<sub [::config/env-name]) "prod")
                          (not @*acknowledged?))]
      [:<>
       (if needs-ack?
         [production-overlay *acknowledged?]
         [acommon/section {:title "Query"}
          [:div.mb-4
           [ui/text-area {:id          "query-input"
                          :label-text  "Datalog query (EDN)"
                          :rows        8
                          :value       @*query
                          :on-change   #(reset! *query (futil/tval %))
                          :placeholder example-query
                          :class       "font-mono"}]]
          [:div.mb-4
           [ui/text-area {:id          "args-input"
                          :label-text  "Additional args (one EDN value per line, bound after $ db)"
                          :rows        3
                          :value       @*args
                          :on-change   #(reset! *args (futil/tval %))
                          :placeholder "\"some-value\"\n42"
                          :class       "font-mono"}]]
          [:div.flex.gap-3.items-center
           [ui/button {:on-click (fn []
                                   (let [args-str @*args
                                         args-vec (some->> (seq args-str)
                                                           str/split-lines
                                                           (map str/trim)
                                                           (remove str/blank?)
                                                           vec)]
                                     (>evt [::execute @*query args-vec])))
                       :disabled (or pending? (empty? @*query))
                       :kind     :primary
                       :size     :field}
            "Analyze"]
           [ui/button {:on-click #(do (reset! *query "")
                                      (reset! *args "")
                                      (>evt [::clear]))
                       :kind     :ghost
                       :size     :field}
            "Clear"]
           (when pending?
             (cc/loading-spinner {:with-overlay false :small true}))]
          (when error
            [:div.mt-4
             [ui/warning error]])])

       (when results
         [:div.mt-4
          [results-summary]])

       [io-stats-display]
       [query-stats-display]
       [results-table]])))

(defn view [match]
  [acommon/admin-page (util/mmap {:title "Datomic Query Analyzer"} match)
   [query-analyzer-panel]])
